import { DeleteOutline, Warning } from "@mui/icons-material";
import { Box, InputBase, Skeleton, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { formatCurrency } from "pdf-generator";
import { ModuleDropDown, SelectBox } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";

export const entityOptions = [
    { value: 'proposal', label: 'Proposal' },
    { value: 'product', label: 'Product' }
];

export const QuoteDetailColumn = (onChange: any, handleDeleteClick: any, calculatingId: string, calculating: boolean, calculateError: boolean, currency: string, isViewOnly?: boolean): GridColDef[] => ([
    {
        field: 'entityType', headerName: 'Entity Type', width: 180, disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return <Box sx={{
                width: "100%",
            }}><SelectBox
                    id="item-detail-entityType"
                    isPaginate
                    value={params.row.entityType}
                    loading={false}
                    loadOptions={() => ({ options: entityOptions, hasMore: false })}
                    options={[]}
                    onChange={(data: SelectBoxOption | null) => {
                        onChange(params.row.id, 'entityType', data)
                    }}
                /></Box>
        }
    },
    {
        field: 'entity', headerName: 'Entity', width: 230, disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return (
                <Box sx={{
                    width: "100%"
                }}>
                    <ModuleDropDown
                        entity={params.row.entityType?.value ?? "none"}
                        value={params.row.entity}
                        disabled={!params.row.entityType?.value}
                        onChange={(option) => onChange(params.row.id, 'entity', option)}
                    />
                </Box>
            );
        }
    },
    {
        field: 'pricing', headerName: 'Pricing', width: 230, disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return (
                <Box sx={{
                    width: "100%"
                }}>
                    <ModuleDropDown
                        entity={"pricing"}
                        value={params.row.pricing}
                        onChange={(option) => onChange(params.row.id, 'pricing', option)}
                        pricingOption={{ object_id: params.row.entity?.value ?? "", object_type: params.row.entityType?.value ?? "" }}
                        pricingFilterCurrency={currency}
                        disableOption={(option) => {
                            let bomCurr = option?.bom_cost?.money?.currency;
                            bomCurr = bomCurr ? bomCurr : `${option.currency_code ?? ''}`;

                            if (bomCurr === currency) {
                                return false;
                            }
                            return true;
                        }}
                        disabled={!params.row.entity?.value}
                    />
                </Box>
            );
        }
    },
    {
        field: 'partNumber', headerName: 'Part/Item No.', width: 230, disableColumnMenu: true, sortable: false
    },
    {
        field: 'description', headerName: 'Description', width: 230, disableColumnMenu: true, sortable: false
    },
    {
        field: 'quantity', disableColumnMenu: true, sortable: false, headerName: 'Quantity', renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                id="item-detail-name"
                placeholder="Quantity"
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                readOnly={isViewOnly}
                value={params.row.quantity}
                onChange={(e) => onChange(params.row.id, 'quantity', e.target.value)}
            />
        }
    },
    {
        field: 'discount', headerName: 'Discount(%)', width: 120, disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                readOnly={isViewOnly}
                id="item-detail-discount"
                placeholder="discount"
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                value={params.row.discount}
                onChange={(e) => onChange(params.row.id, 'discount', e.target.value)}
            />
        }
    },
    {
        field: 'leadTime', headerName: 'Lead Time', disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                readOnly={isViewOnly}
                id="item-detail-lead-time"
                placeholder="Lead time"
                value={params.row.leadTime}
                onChange={(e) => onChange(params.row.id, 'leadTime', e.target.value)}
                onKeyDown={(event) => {
                    event.stopPropagation();
                }}
            />
        }
    },
    {
        field: 'totalCost', headerName: `Total (${currency})`, width: 150, disableColumnMenu: true, sortable: false, renderCell: ({row}) => {

            if (!row.pricing) {
                return ``;
            }

            if (row.pricing?.isCalculated && !row.pricing?.calculating) {
                return formatCurrency(Number((row?.totalCost ?? 0).toFixed(2)), currency, false);
            }

            if (row.pricing?.calculating) {
                return <Tooltip title={`Calculating...`} arrow placement="top">
                    <Skeleton variant="text" width={150} height={40} />
                </Tooltip>;
            }

            if (!row.pricing?.isCalculated) {
                return <Tooltip title={`The pricing for the item is not yet calculated, check the pricing and update before adding in the quote.`} arrow placement="top">
                    <Warning color="warning" />
                </Tooltip>;
            }

            return ''
        }
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: 'Delete',
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <GridActionsCellItem
                    disabled={isViewOnly}
                    icon={<DeleteOutline color={isViewOnly ? "disabled" : "error"} />}
                    label="Delete"
                    onClick={() => handleDeleteClick(id)}
                    color="inherit"
                />,
            ];
        },
    },
]);

export const QuoteCustomDetailColumn = (onChange: any, handleDeleteClick: any, currency: string, isViewOnly?: boolean): GridColDef[] => ([
    {
        field: 'material_id', headerName: 'Entity', width: 230, disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return (
                <Box sx={{
                    width: "100%"
                }}>
                    <ModuleDropDown
                        entity={"material"}
                        value={params.row.material_id}
                        onChange={(option) => onChange(params.row.id, 'material_id', option)}
                        pricingFilterCurrency={currency}
                    />
                </Box>
            );
        }
    },
    {
        field: 'part_number',
        headerName: 'Part Number',
        width: 230,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
            return (
                <InputBase
                    fullWidth
                    id={`item-detail-part_number-${params.row.id}`}
                    placeholder="Part Number"
                    readOnly={isViewOnly || params.row.material_id}
                    value={params.row.part_number || ''}
                    onChange={(e) => onChange(params.row.id, 'part_number', e.target.value)}
                    onKeyDown={(event) => {
                        event.stopPropagation(); // Prevents grid row selection/navigation
                    }}
                />
            );
        }
    },
    {
        field: 'description',
        headerName: 'Description',
        width: 230,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
            return (
                <InputBase
                    fullWidth
                    id={`item-detail-description-${params.row.id}`}
                    placeholder="Description"
                    readOnly={isViewOnly || params.row.material_id}
                    multiline
                    maxRows={2}
                    value={params.row.description || ''}
                    onChange={(e) => onChange(params.row.id, 'description', e.target.value)}
                    onKeyDown={(event) => {
                        event.stopPropagation(); // Prevents grid row selection/navigation
                    }}
                />
            );
        }
    },
    {
        field: 'unitCost', disableColumnMenu: true, sortable: false, headerName: 'Unit Cost', renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                id="item-detail-unit-cost"
                placeholder="Unit Cost"
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                readOnly={isViewOnly || params.row.material_id}
                value={params.row.unitCost}
                onChange={(e) => onChange(params.row.id, 'unitCost', e.target.value)}
                onKeyDown={(event) => {
                    event.stopPropagation();
                }}
            />
        }
    },
    {
        field: 'quantity', disableColumnMenu: true, sortable: false, headerName: 'Quantity', renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                id="item-detail-name"
                placeholder="Quantity"
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                readOnly={isViewOnly}
                value={params.row.quantity}
                onChange={(e) => onChange(params.row.id, 'quantity', e.target.value)}
            />
        }
    },
    {
        field: 'discount', headerName: 'Discount(%)', width: 120, disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                readOnly={isViewOnly}
                id="item-detail-discount"
                placeholder="discount"
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                value={params.row.discount}
                onChange={(e) => onChange(params.row.id, 'discount', e.target.value)}
            />
        }
    },
    {
        field: 'leadTime', headerName: 'Lead Time', disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                readOnly={isViewOnly}
                id="item-detail-lead-time"
                placeholder="Lead time"
                value={params.row.leadTime}
                onKeyDown={(event) => {
                    event.stopPropagation();
                }}
                onChange={(e) => {
                    onChange(params.row.id, 'leadTime', e.target.value)
                }}
            />
        }
    },
    {
        field: 'totalCost', headerName: `Total (${currency})`, width: 200, disableColumnMenu: true, sortable: false, valueGetter: (value, row, column, apiRef) => {
            return formatCurrency(Number((row?.totalCost ?? 0).toFixed(2)), currency, false)
        }
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: 'Delete',
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <GridActionsCellItem
                    disabled={isViewOnly}
                    icon={<DeleteOutline color={isViewOnly ? "disabled" : "error"} />}
                    label="Delete"
                    onClick={() => handleDeleteClick(id)}
                    color="inherit"
                />,
            ];
        },
    },
]);

export const TaxDetailColumn = (onChange: any, handleDeleteClick: any, isViewOnly?: boolean): GridColDef[] => ([
    {
        field: 'description', width: 300, disableColumnMenu: true, sortable: false, headerName: 'Description', renderCell: (params: GridRenderCellParams) => {
            return <InputBase
                id="tax-detail-name"
                placeholder="Description"
                type="text"
                readOnly={isViewOnly}
                value={params.row.description}
                onChange={(e) => onChange(params.row.id, 'description', e.target.value)}
                onKeyDown={(event) => {
                    event.stopPropagation();
                }}
            />
        }
    },
    {
        field: 'value', headerName: 'Value(%)', width: 120, disableColumnMenu: true, sortable: false, renderCell: (params: GridRenderCellParams) => {
            return <InputBase readOnly={isViewOnly} id="tax-detail-value" placeholder="value" onWheel={(e) => (e.target as HTMLElement).blur()} type="number" value={params.row.value} onChange={(e) => onChange(params.row.id, 'value', e.target.value)} />
        }
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: 'Delete',
        cellClassName: 'actions',
        getActions: ({ id }) => {
            return [
                <GridActionsCellItem
                    disabled={isViewOnly}
                    icon={<DeleteOutline color={isViewOnly ? "disabled" : "error"} />}
                    label="Delete"
                    onClick={() => handleDeleteClick(id)}
                    color="inherit"
                />,
            ];
        },
    },
]);
